/**
 * Canvas2DRenderer.ts
 * -----------------------------------------------------------------------------
 * HTML5 canvas-based renderer for DrawingPage objects. Replaces the
 * SVG preview path in the Detailing app with a faster, interactive
 * surface that supports:
 *
 *   - Pan (left-mouse drag, or middle-mouse drag)
 *   - Zoom (mouse wheel, zoomed to the cursor position)
 *   - Fit-to-window (double-click, or via ZoomAll())
 *   - Hi-DPI rendering (multiplies canvas resolution by devicePixelRatio)
 *
 * The API style intentionally mirrors the larger Canvas2D framework
 * (DrawLine / DrawRectangle / SetProperties / DrawProperties) so the
 * call sites read familiarly. We don't depend on the upstream
 * FrameWork / Point2D / Bounds2D / Mouse classes — those would pull
 * in the entire editor stack. Everything here is self-contained.
 *
 * Coordinate convention matches DrawingTypes / PdfWriter:
 *   - DrawingPage's shape coordinates are in PDF points
 *   - Origin top-left
 *   - y increases DOWNWARD
 * No flipping is needed because that's also HTML canvas's native
 * convention. World → screen is just `screenX = worldX * scale + tx`.
 */

import {
  DrawingPage,
  DrawShape,
  SheetView,
  Rgb,
  StrokeStyle,
  FillStyle,
  TextStyle,
  HatchStyle,
} from './io/DrawingTypes';
import { prefersDark, watchTheme } from './theme';

/** Drawing style — line + fill + dash. Loosely mirrors the
 *  uploaded framework's DrawProperties. Internal use only;
 *  external callers pass DrawShape style objects. */
interface DrawProperties {
  linecolor: string;
  fillcolor: string | null;
  thickness: number;
  dashline: number[];
  /** When set, stroke at this WORLD-unit width with round joins/caps instead
   *  of the constant 1px line (used for rebar drawn at its diameter). */
  worldWidth?: number;
  /** When set, stroke at this many SCREEN pixels at any zoom (an annotation
   *  laid over the drawing, e.g. the bridge alignment on an elevation). */
  screenWidth?: number;
  /** When set, rule the interior with parallel lines (concrete hatch). */
  hatch?: HatchStyle;
}

interface TextProperties {
  color: string;
  size: number;
  bold: boolean;
  anchor: 'start' | 'middle' | 'end';
  valign: 'top' | 'middle' | 'bottom';
  rotate: number;
  mono: boolean;
}

/** Canvas2D renderer. Owns an HTML <canvas> element appended into
 *  the supplied container, plus its event wiring for pan + zoom. */
export class Canvas2DRenderer {
  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private page: DrawingPage | null = null;

  // ---- View transform -----------------------------------------
  /** Translation in screen pixels (added after scaling). */
  private viewX: number = 0;
  private viewY: number = 0;
  /** Scale factor — multiplies world points to get screen pixels.
   *  1.0 = 1 PDF point per screen pixel (paper at 72 DPI). */
  private viewScale: number = 1;

  // ---- Hi-DPI -------------------------------------------------
  private dpr: number = Math.max(1, window.devicePixelRatio || 1);
  /** Logical canvas size in CSS pixels (used by every coordinate
   *  computation). The internal canvas resolution is this × dpr. */
  private cssW: number = 0;
  private cssH: number = 0;

  // ---- Interaction state --------------------------------------
  private isPanning: boolean = false;
  private panStartX: number = 0;
  private panStartY: number = 0;
  private panOrigViewX: number = 0;
  private panOrigViewY: number = 0;
  /** Mouse button pressed at the start of the current gesture. */
  private downButton: number = -1;

  // ---- Smooth (decelerating) wheel zoom -----------------------
  /** Scale the view is easing toward. */
  private zoomTarget: number = 1;
  /** Anchor pixel (kept stationary) + the world point under it. */
  private zoomAnchorX: number = 0;
  private zoomAnchorY: number = 0;
  private zoomWorldX: number = 0;
  private zoomWorldY: number = 0;
  /** requestAnimationFrame handle for the easing loop (0 = idle). */
  private zoomRAF: number = 0;

  /** Called on a left-click that wasn't a drag, with the clicked point in
   *  WORLD (page) coordinates. Hosts hit-test their geometry against it to
   *  drive selection. Null = clicks do nothing beyond pan/zoom. */
  public onPick: ((world: { x: number; y: number }) => void) | null = null;

  // ---- Sheet editing (the Drawing pane's composed sheet) -------
  //
  // A composed sheet arrives with its views declared (DrawingPage.views): the
  // box each one occupies, and which shapes it is drawn from. That is all this
  // needs to let the reader draft the sheet by hand — pick a view up, drop it
  // somewhere else, right-click it for its scale — without knowing anything
  // about what a drawing IS. The layout itself stays in C#: a drop reports the
  // distance moved and the host re-composes the page around it.

  /** Let the reader move and re-scale the views. Off for every drawing that is
   *  a single view, where there is nothing to lay out. */
  public sheetEditable: boolean = false;
  /** A view was dropped: its id and how far it travelled, in PAGE units. */
  public onViewMove: ((id: string, dx: number, dy: number) => void) | null = null;
  /** A view was right-clicked (without a drag): the view, and where on the
   *  window to put the menu. */
  public onViewMenu: ((view: SheetView, clientX: number, clientY: number) => void) | null = null;

  /** The view under the cursor, outlined so it is plain what a drag would take
   *  hold of. */
  private hoverView: SheetView | null = null;
  /** The view being dragged, and how far it has come in page units. Kept after
   *  the drop so the drawing does not jump back while the host re-composes the
   *  sheet; cleared when the new page arrives. */
  private dragView: SheetView | null = null;
  private dragDX: number = 0;
  private dragDY: number = 0;
  /** Per-shape: is this one of the dragged view's? Built once per drag so the
   *  paint loop stays a straight pass over the shapes. */
  private dragMask: boolean[] | null = null;
  /** True between mousedown on a view and the mouseup that drops it. */
  private draggingView: boolean = false;

  /** Resize observer that re-fits when the host pane changes size. */
  private resizeObserver: ResizeObserver | null = null;
  /** Bounded rAF retry counter used by Resize() when the container reports a
   *  zero size (mounted before layout settled). Reset once a real size is read. */
  private sizeRetries: number = 0;
  /** Auto-fit on next render? Set by ZoomAll() and on container
   *  resize while no user-driven pan/zoom has happened. */
  private pendingAutoFit: boolean = true;
  /** Has the READER moved the view (dragged a pan, wheeled or clicked a zoom
   *  button)? Until they have, the drawing belongs to the BOX: every paint
   *  re-fits it, so it cannot be left at a scale that was right for some
   *  earlier size — a panel widening as the Explorer collapses, a card
   *  animating open, a pane dragged narrower, a torn-off window resized, or a
   *  redraw whose sheet came out a different size. Once the reader HAS moved
   *  the view it is theirs and nothing re-frames it; ZoomAll (or a new page)
   *  hands it back. */
  private viewMovedByUser: boolean = false;
  /** When true, skip the dark workspace fill + white paper rect +
   *  page border. Used by the modeler's 2D-ortho overlay so the
   *  drawing reads as a chrome-less projection over the dark
   *  viewport rather than as a sheet of paper. */
  private transparent: boolean = false;

  // ---- Theme --------------------------------------------------
  /** Dark mode follows the page's effective colour scheme (see theme.ts —
   *  Settings ▸ UI Theme, else the OS), matching the 3D viewport. When set, the
   *  background flips to a dark surface and every shape colour has its lightness
   *  inverted so the dark CAD ink authored for a white sheet reads as light ink. */
  private dark: boolean = false;
  /** Unhooks the theme listeners (see theme.ts). */
  private themeOff: (() => void) | null = null;

  // ---- Background coordinate grid -----------------------------
  /** Show the zoom-adaptive measurement grid (major + minor lines). */
  public showGrid: boolean = true;
  /** Show the faint minor grid lines (major/5 spacing). */
  public showMinorGrid: boolean = true;
  /** Show the model X=0 / Y=0 axis lines. */
  public showAxis: boolean = true;
  /** Show the bottom (X) + left (Y) rulers pinned to the edges. */
  public showRuler: boolean = true;
  /** Selection marker (callout banner) shown at a page-coord point, matching the
   *  3D floating marker. Null = no selection. Set by the host. */
  public selMarker: { x: number; y: number; label: string } | null = null;
  /** Page coords of the model origin (0,0), from DrawingPage.origin. */
  private origin: { x: number; y: number } | null = null;

  constructor(container: HTMLElement, options?: { transparent?: boolean }) {
    this.container = container;
    this.transparent = options?.transparent === true;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'drw-canvas';
    this.canvas.tabIndex = 0;
    container.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas2DRenderer: 2D context unavailable');
    }
    this.ctx = ctx;
    this.WireEvents();
    this.WireResize();
    this.WireTheme();
    // Defer initial size so the container has its CSS dimensions.
    requestAnimationFrame(() => this.Resize());
  }

  /** Tear down. Removes the canvas + event listeners. */
  public Dispose(): void {
    this.StopZoomAnim();
    this.themeOff?.();
    this.themeOff = null;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }

  /** Set the page to render. Re-fits on the next render (the
   *  Detailing view always wants the new drawing to fill the
   *  window when switching tabs). */
  public SetPage(page: DrawingPage): void {
    this.page = page;
    this.origin = page.origin ?? null;
    this.ClearViewDrag();
    this.pendingAutoFit = true;
    this.viewMovedByUser = false;
    // A new page replaces all content; the host re-establishes the selection
    // marker for plan pages via updatePlanMarker().
    this.selMarker = null;
    this.Render();
  }

  /** Replace the page WITHOUT re-fitting — keeps the current pan/zoom. Used when
   *  the host re-exports the same drawing with different options (dimension
   *  toggles / text size) so the view doesn't jump. */
  public SetPageKeepView(page: DrawingPage): void {
    this.page = page;
    this.origin = page.origin ?? null;
    // The page that comes back after a drop has the move in it, so the local
    // offset that was standing in for it goes.
    this.ClearViewDrag();
    this.Render();
  }

  // ============================================================
  // Sheet editing
  // ============================================================

  /** Forget any drag offset and hover outline — a new page is a new layout. */
  private ClearViewDrag(): void {
    this.dragView = null;
    this.dragMask = null;
    this.dragDX = 0;
    this.dragDY = 0;
    this.draggingView = false;
    this.hoverView = null;
  }

  /** The view at a page point, or null. The SMALLEST box containing the point
   *  wins, so a detail sitting inside the sweep of a long elevation's box is
   *  the one the reader picks up. */
  private ViewAt(world: { x: number; y: number }): SheetView | null {
    if (!this.sheetEditable) return null;
    const views = this.page?.views;
    if (!views || views.length === 0) return null;

    let best: SheetView | null = null;
    let bestArea = Infinity;

    for (const v of views) {
      const off = v === this.dragView ? { x: this.dragDX, y: this.dragDY } : { x: 0, y: 0 };
      if (world.x < v.x + off.x || world.x > v.x + off.x + v.w) continue;
      if (world.y < v.y + off.y || world.y > v.y + off.y + v.h) continue;
      const area = Math.max(v.w * v.h, 1e-9);
      if (area < bestArea) {
        best = v;
        bestArea = area;
      }
    }

    return best;
  }

  /** Outline one view — dashed while it is merely under the cursor, solid while
   *  it is being dragged. Drawn in world space at a screen-constant weight, so
   *  it reads the same at any zoom. */
  private DrawViewOutline(v: SheetView, dragging: boolean): void {
    const ctx = this.ctx;
    const off = v === this.dragView ? { x: this.dragDX, y: this.dragDY } : { x: 0, y: 0 };
    const px = 1 / this.viewScale;
    ctx.save();
    ctx.lineWidth = (dragging ? 1.6 : 1.2) * px;
    ctx.setLineDash(dragging ? [] : [6 * px, 4 * px]);
    ctx.strokeStyle = this.dark ? 'rgba(120,180,255,0.95)' : 'rgba(20,90,200,0.85)';
    ctx.strokeRect(v.x + off.x, v.y + off.y, v.w, v.h);
    ctx.restore();
  }

  /** The page currently displayed (for export), or null. */
  public GetPage(): DrawingPage | null {
    return this.page;
  }

  /** True when the preview is painting in dark mode (see the theme wiring), so
   *  an exporter can match the sheet the user is actually looking at. */
  public get isDark(): boolean {
    return this.dark;
  }

  /**
   * The current view as a PNG data URL — exactly what is on screen, including
   * pan, zoom, grid, rulers and theme.
   *
   * `scale` multiplies the device pixel ratio while the capture renders, so the
   * file comes out sharper than the (often small) preview pane. Only `Resize()`
   * reads `dpr`, and it recomputes the backing store from the CSS size without
   * touching the view transform, so bumping it and putting it back is a
   * complete round trip. Both renders are synchronous, so no intermediate frame
   * reaches the screen.
   *
   * Returns '' when the canvas has no measurable size (mounted but not laid
   * out), rather than a blank image.
   */
  public ToPngDataUrl(scale: number = 3): string {
    if (this.cssW <= 0 || this.cssH <= 0) return '';
    const prev = this.dpr;
    // Cap the backing store at the canvas 2D limit most engines enforce
    // (~32k on a side, and a total area budget well below that).
    const maxSide = 8192;
    const k = Math.min(
      Math.max(scale, 1),
      maxSide / Math.max(this.cssW, this.cssH) / prev,
    );
    try {
      this.dpr = prev * Math.max(k, 1);
      this.Resize();
      return this.canvas.toDataURL('image/png');
    } finally {
      this.dpr = prev;
      this.Resize();
    }
  }

  /** The host element this renderer's canvas is attached to. Lets callers detect
   *  a stale renderer whose host was recreated by the UI framework. */
  public get hostElement(): HTMLElement {
    return this.container;
  }

  /** Toggle the background grid / minor grid / axis, then repaint. */
  public SetGridOptions(opts: {
    grid?: boolean;
    minor?: boolean;
    axis?: boolean;
    ruler?: boolean;
  }): void {
    if (opts.grid !== undefined) this.showGrid = opts.grid;
    if (opts.minor !== undefined) this.showMinorGrid = opts.minor;
    if (opts.axis !== undefined) this.showAxis = opts.axis;
    if (opts.ruler !== undefined) this.showRuler = opts.ruler;
    this.Render();
  }

  /** Compute view-transform that fits the entire page into the
   *  canvas with a small margin, then render. */
  public ZoomAll(): void {
    this.StopZoomAnim();
    this.pendingAutoFit = true;
    // Fit-to-window is also how the reader gives the view back: from here on the
    // drawing re-fits itself as the box changes size, until they move it again.
    this.viewMovedByUser = false;
    this.Render();
  }

  /** Multiplicative zoom centred on the canvas centre (eased). */
  public ZoomIn(): void {
    this.ZoomBy(1.25, this.cssW / 2, this.cssH / 2);
  }
  public ZoomOut(): void {
    this.ZoomBy(1 / 1.25, this.cssW / 2, this.cssH / 2);
  }

  /** Render the current page. No-op when no page is set or the
   *  canvas has zero size. */
  public Render(): void {
    if (!this.page || this.cssW <= 0 || this.cssH <= 0) return;
    // While the view is still the AUTOMATIC one, "fitted" is an invariant, not a
    // one-shot: re-derive it on every paint. Fitting once was the bug behind both
    // ways a drawing could end up unreadable — a box that grew after the fit left
    // the drawing adrift in white space, and a box that shrank (or a redraw whose
    // sheet came out bigger, which SetPageKeepView deliberately does not re-frame)
    // left it scaled too large and clipped at the edges. FitToWindow is a dozen
    // arithmetic operations, so there is nothing to save by tracking every reason
    // the two could fall out of step.
    if (this.pendingAutoFit || !this.viewMovedByUser) {
      this.FitToWindow();
      this.pendingAutoFit = false;
    }
    const ctx = this.ctx;
    // Clear in CSS-pixel space. Canvas was already scaled by dpr in
    // Resize() so all subsequent drawing is in CSS pixels.
    ctx.save();
    if (this.transparent) {
      // Transparent mode (modeler 2D-ortho overlay): wipe the
      // canvas to fully transparent so the underlying viewport
      // background shows through. No paper rect, no border, no
      // drop shadow.
      ctx.clearRect(0, 0, this.cssW, this.cssH);
    } else {
      // Fill the entire canvas — no separate "paper" sheet, shadow or border;
      // the whole 2D background is one flat surface (white in light mode, a
      // dark surface in dark mode, matching the .mdl-canvas2d CSS token).
      ctx.fillStyle = this.dark ? DARK_BG : '#ffffff';
      ctx.fillRect(0, 0, this.cssW, this.cssH);
    }

    // Set up the world transform: translate then scale, so every
    // (worldX, worldY) draws at (worldX * scale + viewX, ...).
    ctx.translate(this.viewX, this.viewY);
    ctx.scale(this.viewScale, this.viewScale);

    // No page-rect clip: the whole canvas is white, so let shapes that sit
    // just outside the page extent (e.g. grid bubbles at the margins) draw in
    // full instead of being cut off at the page edges.

    // Background coordinate grid + axis, drawn first so it sits behind the
    // structural content. Skipped in transparent (modeler overlay) mode.
    if (this.showGrid && !this.transparent) this.DrawGrid();

    // Draw every shape in array order (later = on top). A view being dragged is
    // drawn LAST and translated, which is the whole of the live preview: the
    // sheet stays where it is and the one view travels over it.
    const mask = this.dragView ? this.dragMask : null;

    if (!mask) {
      for (const shape of this.page.shapes) {
        this.DrawShape(shape);
      }
    } else {
      const shapes = this.page.shapes;
      for (let i = 0; i < shapes.length; i++) {
        if (!mask[i]) this.DrawShape(shapes[i]);
      }
      ctx.save();
      ctx.translate(this.dragDX, this.dragDY);
      for (let i = 0; i < shapes.length; i++) {
        if (mask[i]) this.DrawShape(shapes[i]);
      }
      ctx.restore();
    }

    // What a drag would take hold of, or what one has hold of.
    if (this.sheetEditable) {
      if (this.dragView) this.DrawViewOutline(this.dragView, true);
      else if (this.hoverView) this.DrawViewOutline(this.hoverView, false);
    }

    ctx.restore();

    // Rulers are drawn last, in screen space (no view transform), so they
    // stay pinned to the top + bottom edges regardless of pan/zoom.
    if (this.showRuler && !this.transparent) this.DrawRulers();

    // Selection marker on top of everything, in screen space (constant size).
    this.DrawSelectionMarker();
  }

  /** Draw the selection callout (dark banner + downward pointer) in screen space
   *  at the marker's page point, at a constant on-screen size. */
  private DrawSelectionMarker(): void {
    const m = this.selMarker;
    if (!m) return;
    const ctx = this.ctx;
    const sx = m.x * this.viewScale + this.viewX;
    const sy = m.y * this.viewScale + this.viewY;
    ctx.save();
    ctx.font = 'bold 13px Helvetica, Arial, sans-serif';
    const tw = ctx.measureText(m.label).width;
    const padX = 8;
    const bh = 22;
    const ph = 8;
    const r = 6;
    const w = Math.max(24, tw + padX * 2);
    const left = sx - w / 2;
    const right = sx + w / 2;
    const bottom = sy - ph; // banner bottom; pointer tip at (sx, sy)
    const top = bottom - bh;
    ctx.beginPath();
    ctx.moveTo(left + r, top);
    ctx.lineTo(right - r, top);
    ctx.arcTo(right, top, right, top + r, r);
    ctx.lineTo(right, bottom - r);
    ctx.arcTo(right, bottom, right - r, bottom, r);
    ctx.lineTo(sx + 6, bottom);
    ctx.lineTo(sx, sy); // pointer tip
    ctx.lineTo(sx - 6, bottom);
    ctx.lineTo(left + r, bottom);
    ctx.arcTo(left, bottom, left, bottom - r, r);
    ctx.lineTo(left, top + r);
    ctx.arcTo(left, top, left + r, top, r);
    ctx.closePath();
    ctx.fillStyle = '#5a1009';
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(m.label, sx, top + bh / 2);
    ctx.restore();
  }

  /** Draw fixed bottom (model-X) + left (model-Y) rulers, with coordinate
   *  ticks + labels aligned to the major grid. Screen-pixel space; called after
   *  the view transform is restored so the bands stay pinned to the edges. The
   *  X ruler sits at the bottom because the top is occupied by the story-filter
   *  overlay. */
  private DrawRulers(): void {
    const ctx = this.ctx;
    const size = 22; // band thickness (CSS px)
    const ox = this.origin ? this.origin.x : 0;
    const oy = this.origin ? this.origin.y : 0;
    const major = niceInterval(80 / this.viewScale);
    const botY = this.cssH - size; // top edge of the bottom band

    ctx.save();
    ctx.setLineDash([]);

    // Bands.
    ctx.fillStyle = this.dark ? 'rgba(42,42,42,0.96)' : 'rgba(246,248,250,0.96)';
    ctx.fillRect(0, 0, size, this.cssH); // left
    ctx.fillRect(0, botY, this.cssW, size); // bottom

    // Separator lines along each band's inner edge.
    ctx.strokeStyle = this.dark ? 'rgb(70,70,70)' : 'rgb(199,205,212)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, botY + 0.5);
    ctx.lineTo(this.cssW, botY + 0.5);
    ctx.moveTo(size + 0.5, 0);
    ctx.lineTo(size + 0.5, this.cssH);
    ctx.stroke();

    ctx.fillStyle = this.dark ? 'rgb(170,176,184)' : 'rgb(92,102,112)';
    ctx.strokeStyle = this.dark ? 'rgb(120,128,138)' : 'rgb(150,160,170)';
    ctx.font = '10px Helvetica, Arial, sans-serif';

    // Visible world range.
    const left = -this.viewX / this.viewScale;
    const right = (this.cssW - this.viewX) / this.viewScale;
    const top = -this.viewY / this.viewScale;
    const bottom = (this.cssH - this.viewY) / this.viewScale;

    // --- Bottom ruler: model X ticks (skip the left-band corner) ---
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const kx = Math.ceil((left - ox) / major);
    for (let wx = ox + kx * major; wx <= right; wx += major) {
      const sx = wx * this.viewScale + this.viewX;
      if (sx < size || sx > this.cssW) continue;
      const px = Math.round(sx) + 0.5;
      ctx.beginPath();
      ctx.moveTo(px, botY);
      ctx.lineTo(px, botY + 6);
      ctx.stroke();
      ctx.fillText(String(Math.round(wx - ox)), sx, this.cssH - 4);
    }

    // --- Left ruler: model Y ticks (rotated labels; skip the bottom corner) ---
    const ky = Math.ceil((top - oy) / major);
    for (let wy = oy + ky * major; wy <= bottom; wy += major) {
      const sy = wy * this.viewScale + this.viewY;
      if (sy < 0 || sy > botY) continue;
      const py = Math.round(sy) + 0.5;
      ctx.beginPath();
      ctx.moveTo(size - 6, py);
      ctx.lineTo(size, py);
      ctx.stroke();
      // Model Y increases upward, so it's the page-Y origin minus world Y.
      const label = String(Math.round(oy - wy));
      ctx.save();
      ctx.translate(11, sy);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
    ctx.restore();
  }

  // ============================================================
  // Background coordinate grid
  // ============================================================

  /** Draw a zoom-adaptive measurement grid (minor + major) and the model
   *  X=0 / Y=0 axis, aligned to the model origin. Runs inside the world
   *  transform, so coordinates are page (= model mm) units; line widths are
   *  divided by the scale to stay crisp 1px regardless of zoom. */
  private DrawGrid(): void {
    const ctx = this.ctx;
    // Visible region in world (page) coordinates.
    const left = -this.viewX / this.viewScale;
    const right = (this.cssW - this.viewX) / this.viewScale;
    const top = -this.viewY / this.viewScale;
    const bottom = (this.cssH - this.viewY) / this.viewScale;

    // Model origin in page coords (fall back to page origin if unknown).
    const ox = this.origin ? this.origin.x : 0;
    const oy = this.origin ? this.origin.y : 0;

    // Pick a "nice" major spacing so its on-screen size is ~80px, with the
    // minor grid at a fifth of that.
    const major = niceInterval(80 / this.viewScale);
    const minor = major / 5;
    const onepx = 1 / this.viewScale;

    ctx.save();
    ctx.setLineDash([]);

    // Minor grid (only when it won't be too dense on screen).
    if (this.showMinorGrid && minor * this.viewScale >= 7) {
      ctx.strokeStyle = this.dark ? 'rgba(255,255,255,0.05)' : 'rgb(237,239,242)';
      ctx.lineWidth = onepx;
      this.GridLines(left, right, top, bottom, ox, oy, minor);
    }

    // Major grid.
    ctx.strokeStyle = this.dark ? 'rgba(255,255,255,0.11)' : 'rgb(210,216,222)';
    ctx.lineWidth = onepx;
    this.GridLines(left, right, top, bottom, ox, oy, major);

    // Axis (model X=0 vertical, model Y=0 horizontal).
    if (this.showAxis) {
      ctx.strokeStyle = this.dark ? 'rgba(255,255,255,0.22)' : 'rgb(150,160,171)';
      ctx.lineWidth = 1.5 * onepx;
      if (ox >= left && ox <= right) {
        ctx.beginPath();
        ctx.moveTo(ox, top);
        ctx.lineTo(ox, bottom);
        ctx.stroke();
      }
      if (oy >= top && oy <= bottom) {
        ctx.beginPath();
        ctx.moveTo(left, oy);
        ctx.lineTo(right, oy);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** Stroke all grid lines at `step` spacing within the visible rect, aligned
   *  so a line passes through the origin (ox, oy). Batched in one path. */
  private GridLines(
    left: number,
    right: number,
    top: number,
    bottom: number,
    ox: number,
    oy: number,
    step: number,
  ): void {
    if (step <= 0 || !isFinite(step)) return;
    const ctx = this.ctx;
    ctx.beginPath();
    const kx = Math.ceil((left - ox) / step);
    for (let x = ox + kx * step; x <= right; x += step) {
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
    }
    const ky = Math.ceil((top - oy) / step);
    for (let y = oy + ky * step; y <= bottom; y += step) {
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }
    ctx.stroke();
  }

  // ============================================================
  // Shape rendering
  // ============================================================

  private DrawShape(shape: DrawShape): void {
    switch (shape.kind) {
      case 'line':
        this.DrawLine(
          shape.x1,
          shape.y1,
          shape.x2,
          shape.y2,
          this.PropertiesForStroke(shape.style),
        );
        break;
      case 'rect':
        this.DrawRectangle(
          shape.x,
          shape.y,
          shape.w,
          shape.h,
          this.PropertiesForFill(shape.style),
        );
        break;
      case 'polygon':
        this.DrawPolygon(
          shape.points,
          this.PropertiesForFill(shape.style),
          true,
          shape.holes,
        );
        break;
      case 'polyline':
        this.DrawPolygon(
          shape.points,
          this.PropertiesForStroke(shape.style),
          false,
        );
        break;
      case 'circle':
        this.DrawCircle(
          shape.cx,
          shape.cy,
          shape.r,
          this.PropertiesForFill(shape.style),
        );
        break;
      case 'text':
        this.DrawText(shape.x, shape.y, shape.text, shape.style);
        break;
    }
  }

  /** Stroke a single line segment. The framework's API style uses
   *  `DrawLine(x1, y1, x2, y2, props)` — we mirror that even though
   *  the underlying canvas API takes moveTo + lineTo. */
  private DrawLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    props: DrawProperties,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    if (props.worldWidth && props.worldWidth > 0) {
      // True world-width stroke (e.g. a rebar drawn at its diameter) with round
      // caps, instead of the constant ~1px CAD line.
      const prevCap = ctx.lineCap;
      ctx.lineCap = 'round';
      ctx.lineWidth = props.worldWidth;
      ctx.strokeStyle = props.linecolor;
      this.ApplyDash([]);
      ctx.stroke();
      ctx.lineCap = prevCap;
    } else if (props.screenWidth && props.screenWidth > 1) {
      const prevCap = ctx.lineCap;
      ctx.lineCap = 'round';
      ctx.lineWidth = this.ScreenLineWidth(props.screenWidth);
      ctx.strokeStyle = props.linecolor;
      this.ApplyDash(props.dashline);
      ctx.stroke();
      ctx.lineCap = prevCap;
    } else {
      ctx.lineWidth = this.MinLineWidth(props.thickness);
      ctx.strokeStyle = props.linecolor;
      this.ApplyDash(props.dashline);
      ctx.stroke();
    }
  }

  private DrawRectangle(
    x: number,
    y: number,
    w: number,
    h: number,
    props: DrawProperties,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    if (props.fillcolor) {
      ctx.fillStyle = props.fillcolor;
      ctx.fill();
    }
    if (props.linecolor) {
      ctx.lineWidth = this.OutlineWidth(props);
      ctx.strokeStyle = props.linecolor;
      this.ApplyDash(props.dashline);
      ctx.stroke();
    }
  }

  /** Outline width for a filled shape: the constant-weight `screenWidth` when
   *  the style asked for one, otherwise the usual 1-screen-pixel CAD line. */
  private OutlineWidth(props: DrawProperties): number {
    return props.screenWidth && props.screenWidth > 1
      ? this.ScreenLineWidth(props.screenWidth)
      : this.MinLineWidth(props.thickness);
  }

  /** Polygon (closed) or polyline (open), depending on `close`. Optional
   *  `holes` are traced as extra subpaths and filled even-odd, so a closed
   *  core wall reads as a hollow ring. */
  private DrawPolygon(
    points: ReadonlyArray<readonly [number, number]>,
    props: DrawProperties,
    close: boolean,
    holes?: ReadonlyArray<ReadonlyArray<readonly [number, number]>>,
  ): void {
    if (points.length < 2) return;
    const ctx = this.ctx;
    const trace = (pts: ReadonlyArray<readonly [number, number]>): void => {
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      if (close) ctx.closePath();
    };
    const traceAll = (): void => {
      ctx.beginPath();
      trace(points);
      if (close && holes) {
        for (const h of holes) if (h.length >= 3) trace(h);
      }
    };
    traceAll();
    if (close && props.fillcolor) {
      ctx.fillStyle = props.fillcolor;
      ctx.fill('evenodd');
    }
    // Hatch before the outline, so the profile line stays the heaviest thing on
    // the shape and the ruling stops cleanly at it.
    if (close && props.hatch) {
      this.DrawHatch(traceAll, points, holes, props.hatch);
      traceAll();
    }
    if (props.linecolor) {
      if (props.worldWidth && props.worldWidth > 0) {
        // True world-width stroke (e.g. a rebar at its diameter): round joins
        // and caps so bends read as smooth bars rather than mitred lines.
        const prevJoin = ctx.lineJoin;
        const prevCap = ctx.lineCap;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.lineWidth = props.worldWidth;
        ctx.strokeStyle = props.linecolor;
        this.ApplyDash([]);
        ctx.stroke();
        ctx.lineJoin = prevJoin;
        ctx.lineCap = prevCap;
      } else if (props.screenWidth && props.screenWidth > 1) {
        // Constant-weight annotation stroke laid over the drawing.
        const prevJoin = ctx.lineJoin;
        const prevCap = ctx.lineCap;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.lineWidth = this.ScreenLineWidth(props.screenWidth);
        ctx.strokeStyle = props.linecolor;
        this.ApplyDash(props.dashline);
        ctx.stroke();
        ctx.lineJoin = prevJoin;
        ctx.lineCap = prevCap;
      } else {
        ctx.lineWidth = this.MinLineWidth(props.thickness);
        ctx.strokeStyle = props.linecolor;
        this.ApplyDash(props.dashline);
        ctx.stroke();
      }
    }
  }

  /**
   * Rule the current shape with parallel hatch lines — concrete, the way a
   * section drawing draws it.
   *
   * The ruling is CLIPPED to the shape rather than computed against its edges:
   * canvas clipping already understands the even-odd rule the fill uses, so a
   * profile with holes hatches correctly with no polygon intersection code.
   * Lines are laid out in a rotated frame centred on the shape, long enough to
   * span its diagonal from any angle, and drawn at the same 1-screen-pixel
   * weight as the rest of the drawing so the hatch never thickens into a smear
   * as the reader zooms in.
   *
   * Spacing is in WORLD units, which is what the SVG <pattern> this comes from
   * uses (patternUnits="userSpaceOnUse") — so the hatch scales with the section
   * exactly as it does in the inline-SVG copy.
   */
  private DrawHatch(
    traceAll: () => void,
    points: ReadonlyArray<readonly [number, number]>,
    holes: ReadonlyArray<ReadonlyArray<readonly [number, number]>> | undefined,
    hatch: HatchStyle,
  ): void {
    const spacing = hatch.spacing > 0 ? hatch.spacing : 7;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const grow = (pts: ReadonlyArray<readonly [number, number]>): void => {
      for (const [x, y] of pts) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    };
    grow(points);
    if (holes) for (const h of holes) grow(h);
    if (!isFinite(minX) || maxX <= minX || maxY <= minY) return;

    const w = maxX - minX;
    const h = maxY - minY;
    // Half-diagonal + one line of slack: the ruling covers the shape whatever
    // the angle, without needing to know it.
    const reach = Math.hypot(w, h) / 2 + spacing;
    // A very small shape at a very fine spacing would otherwise queue tens of
    // thousands of segments per frame for a solid grey result.
    const lines = Math.ceil((reach * 2) / spacing);
    if (lines > 4000) return;

    const ctx = this.ctx;
    ctx.save();
    traceAll();
    ctx.clip('evenodd');
    ctx.translate(minX + w / 2, minY + h / 2);
    ctx.rotate((hatch.angle * Math.PI) / 180);
    ctx.beginPath();
    for (let d = -reach; d <= reach; d += spacing) {
      ctx.moveTo(-reach, d);
      ctx.lineTo(reach, d);
    }
    ctx.lineWidth = this.MinLineWidth(0);
    ctx.strokeStyle = this.Css(hatch.color ?? INK);
    this.ApplyDash([]);
    ctx.stroke();
    ctx.restore();
  }

  private DrawCircle(
    cx: number,
    cy: number,
    r: number,
    props: DrawProperties,
  ): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    if (props.fillcolor) {
      ctx.fillStyle = props.fillcolor;
      ctx.fill();
    }
    if (props.linecolor) {
      ctx.lineWidth = this.OutlineWidth(props);
      ctx.strokeStyle = props.linecolor;
      this.ApplyDash(props.dashline);
      ctx.stroke();
    }
  }

  private DrawText(
    x: number,
    y: number,
    text: string,
    style: TextStyle | undefined,
  ): void {
    const props = this.PropertiesForText(style);
    const ctx = this.ctx;
    const fontWeight = props.bold ? '700' : '400';
    // Convert the DrawShape y to the canvas baseline. Default 'top' = y is the
    // top of the glyph cell (+0.8 size); 'bottom' = text sits above y (baseline
    // at y); 'middle' = roughly centred on y.
    const baseline =
      props.valign === 'bottom'
        ? y
        : props.valign === 'middle'
          ? y + props.size * 0.35
          : y + props.size * 0.8;
    const face = props.mono
      ? 'Consolas, "Cascadia Mono", ui-monospace, monospace'
      : 'Helvetica, Arial, sans-serif';
    ctx.font = `${fontWeight} ${props.size}px ${face}`;
    ctx.fillStyle = props.color;
    const align =
      props.anchor === 'middle' ? 'center' : props.anchor === 'end' ? 'right' : 'left';
    if (props.rotate) {
      // Rotate about the (x, y) anchor. Page Y is down, so a positive (CCW)
      // angle maps to a negative canvas rotation. Centre the glyph on y.
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((-props.rotate * Math.PI) / 180);
      ctx.textBaseline = 'middle';
      ctx.textAlign = align;
      ctx.fillText(text, 0, 0);
      ctx.restore();
      return;
    }
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = align;
    ctx.fillText(text, x, baseline);
  }

  // ============================================================
  // DrawShape style → DrawProperties translation
  // ============================================================

  private PropertiesForStroke(style: StrokeStyle | undefined): DrawProperties {
    return {
      linecolor: this.Css(style?.color ?? INK),
      fillcolor: null,
      thickness: style?.width ?? 0.5,
      dashline: style?.dash ? [...style.dash] : [],
      worldWidth: style?.worldWidth,
      screenWidth: style?.screenWidth,
    };
  }

  private PropertiesForFill(style: FillStyle | undefined): DrawProperties {
    return {
      linecolor: style?.stroke ? this.Css(style.stroke) : '',
      fillcolor: style?.fill ? this.Css(style.fill) : null,
      thickness: style?.width ?? 0.5,
      dashline: style?.dash ? [...style.dash] : [],
      screenWidth: style?.screenWidth,
      hatch: style?.hatch,
    };
  }

  private PropertiesForText(style: TextStyle | undefined): TextProperties {
    return {
      color: this.Css(style?.color ?? INK),
      size: style?.size ?? 9,
      bold: !!style?.bold,
      anchor: style?.anchor ?? 'start',
      valign: style?.valign ?? 'top',
      rotate: style?.rotate ?? 0,
      mono: !!style?.mono,
    };
  }

  /** A shape colour as a CSS string, theme-adapted. In dark mode each colour's
   *  lightness is inverted (hue + saturation preserved) so the dark ink authored
   *  for a white sheet reads as light ink on the dark surface, while genuinely
   *  coloured elements stay coloured. */
  private Css(c: Rgb): string {
    return rgbToCss(this.dark ? invertLightness(c) : c);
  }

  /** Apply a dash pattern to the current context. */
  private ApplyDash(dash: ReadonlyArray<number>): void {
    if (dash.length > 0) {
      this.ctx.setLineDash([...dash]);
    } else {
      this.ctx.setLineDash([]);
    }
  }

  /** Return the lineWidth (in world units) that renders as exactly
   *  1 screen pixel regardless of zoom. The canvas context is
   *  scaled by `viewScale` (so 1 world unit = viewScale screen
   *  pixels), which means a 1-screen-pixel stroke is
   *  `1 / viewScale` in world units.
   *
   *  The `thicknessPoints` parameter is intentionally IGNORED for
   *  on-screen preview — every line draws at a constant 1px so the
   *  drawing reads like a CAD viewport (lines crisp at any zoom,
   *  never shrinking to invisibility or growing into thick bands).
   *  The PDF export still uses the original `thicknessPoints` via
   *  PdfWriter, so the printed output keeps its proper line-weight
   *  hierarchy. */
  private MinLineWidth(thicknessPoints: number): number {
    void thicknessPoints;
    return this.lineScale / this.viewScale;
  }

  /** Multiplier on every screen-constant line weight — the plain CAD line and
   *  the heavier pens the exporter gives geometry (`screenWidth`) alike, so the
   *  drawing keeps its weight hierarchy, just finer. 1 by default. Below 1 lines
   *  draw as anti-aliased hairlines, which is what keeps a dense drawing shown
   *  small (the Property panel's key plan) from closing up into a solid black
   *  band. World-width strokes (a rebar at its diameter) keep their own. */
  private lineScale = 1;

  /** Set the plain line's screen width (see `lineScale`) and redraw. */
  SetLineScale(scale: number): void {
    const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
    if (s === this.lineScale) return;
    this.lineScale = s;
    this.Render();
  }

  /** The lineWidth (in world units) that renders as exactly `px` screen pixels
   *  at the current zoom — the constant-weight counterpart of `worldWidth`,
   *  for an annotation stroke that must not thicken as the reader zooms in. */
  private ScreenLineWidth(px: number): number {
    return (Math.max(px, 1) * this.PenScale()) / this.viewScale;
  }

  /**
   * How much of its on-screen weight a line keeps at the current zoom: 1 at
   * print size and above, less as a SHEET is zoomed out.
   *
   * A constant one-pixel line is right for a single detail, but a whole sheet
   * fitted to a pane packs a plot's worth of linework into a few hundred
   * pixels, and at one pixel each the drawing goes dark and heavy - nothing
   * like the paper it stands for. A sheet's page units are millimetres of
   * paper (`paperUnit`), so the renderer knows when the paper is drawn smaller
   * than it prints, and thins every pen in proportion - the profile stays
   * heavier than the dimension rails, as it does on the plot.
   *
   * Square root, not linear, and floored: a line cut to a quarter of a pixel
   * at a quarter of print size anti-aliases to a near-invisible grey, and the
   * zoomed-out sheet is still being READ for its arrangement. Zooming in never
   * thickens past the one-pixel CAD line.
   */
  private PenScale(): number {
    if (this.page?.paperUnit !== 'mm') return 1;
    const printSize = 96 / 25.4; // CSS px per paper mm at 100%
    const r = this.viewScale / printSize;
    return r >= 1 ? 1 : Math.max(0.3, Math.sqrt(r));
  }

  // ============================================================
  // View transform helpers
  // ============================================================

  /** Auto-fit the page rectangle into the canvas with a 4% margin. */
  private FitToWindow(): void {
    if (!this.page || this.cssW === 0 || this.cssH === 0) return;
    const margin = 0.04;
    const fitW = this.cssW * (1 - margin * 2);
    const fitH = this.cssH * (1 - margin * 2);
    // Fit to an explicit sub-rectangle (isolated selection) if one is given,
    // otherwise the whole page.
    const r = this.page.fitRect;
    const rx = r ? r.x : 0;
    const ry = r ? r.y : 0;
    const rw = r ? r.w : this.page.paperWidth;
    const rh = r ? r.h : this.page.paperHeight;
    this.viewScale = Math.min(fitW / rw, fitH / rh);
    // Centre the rectangle: world point (rx,ry) maps to the top-left of the
    // centred fit box.
    this.viewX = (this.cssW - rw * this.viewScale) / 2 - rx * this.viewScale;
    this.viewY = (this.cssH - rh * this.viewScale) / 2 - ry * this.viewScale;
  }

  /** Smoothly zoom by `factor`, anchored at the CANVAS pixel (cx, cy). The
   *  view eases toward the new scale with per-frame deceleration (so it glides
   *  to a stop instead of snapping), mirroring the 3D viewport's damped wheel
   *  zoom. The world point under the anchor stays put throughout. */
  private ZoomBy(factor: number, cx: number, cy: number): void {
    this.viewMovedByUser = true;
    // Pivot: world point currently under the cursor (from the live transform).
    this.zoomAnchorX = cx;
    this.zoomAnchorY = cy;
    this.zoomWorldX = (cx - this.viewX) / this.viewScale;
    this.zoomWorldY = (cy - this.viewY) / this.viewScale;
    // Accumulate onto the target so rapid scroll ticks compound.
    if (!this.zoomRAF) this.zoomTarget = this.viewScale;
    this.zoomTarget *= factor;
    if (!this.zoomRAF) this.zoomRAF = requestAnimationFrame(this.ZoomStep);
  }

  /** One easing step: move viewScale a fraction of the way to the target,
   *  re-anchor, repaint, and schedule the next frame until settled. */
  private ZoomStep = (): void => {
    const diff = this.zoomTarget - this.viewScale;
    if (Math.abs(diff) <= this.zoomTarget * 0.0015) {
      this.viewScale = this.zoomTarget; // snap the last sliver, then stop
      this.zoomRAF = 0;
    } else {
      this.viewScale += diff * 0.18; // approach ~18%/frame → smooth glide
      this.zoomRAF = requestAnimationFrame(this.ZoomStep);
    }
    // Keep the pivot world point under the anchor pixel as scale changes.
    this.viewX = this.zoomAnchorX - this.zoomWorldX * this.viewScale;
    this.viewY = this.zoomAnchorY - this.zoomWorldY * this.viewScale;
    this.Render();
  };

  /** Cancel any in-flight zoom easing (e.g. when panning or fitting begins). */
  private StopZoomAnim(): void {
    if (this.zoomRAF) {
      cancelAnimationFrame(this.zoomRAF);
      this.zoomRAF = 0;
    }
  }

  /** Convert a client (viewport) pixel coordinate to WORLD (page) coords,
   *  inverting the current pan/zoom transform. */
  public WorldFromClient(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    return {
      x: (sx - this.viewX) / this.viewScale,
      y: (sy - this.viewY) / this.viewScale,
    };
  }

  // ============================================================
  // Sizing + DPR
  // ============================================================

  /** Recompute the canvas internal size from its CSS size + DPR. */
  private Resize(): void {
    // Read the available size from the CONTAINER. Reading from the
    // canvas would only work the first time — once we set
    // canvas.style.width to a fixed pixel value (below, for the
    // hi-DPI pattern), the canvas's bounding rect stops tracking
    // CSS layout changes like a sidebar collapse.
    const rect = this.container.getBoundingClientRect();
    this.cssW = rect.width;
    this.cssH = rect.height;
    if (this.cssW === 0 || this.cssH === 0) {
      // Mounted before the container had a measurable size (initial layout not
      // flushed, or a card still animating open). The ResizeObserver only fires
      // on a size CHANGE, so if the very first measurement races to 0 while the
      // element is actually sized, it would never re-fire and the preview stays
      // blank. Re-measure on the next frames (bounded ~2s) until a real size
      // appears, then render — this is why the inline preview was sometimes blank
      // while the expanded copy (mounted after layout settled) was fine.
      if (this.sizeRetries < 120) {
        this.sizeRetries++;
        requestAnimationFrame(() => this.Resize());
      }
      return;
    }
    this.sizeRetries = 0;
    // Hi-DPI: bump internal resolution, then ctx.scale by DPR so
    // subsequent drawing happens in CSS pixel units.
    this.canvas.width = Math.round(this.cssW * this.dpr);
    this.canvas.height = Math.round(this.cssH * this.dpr);
    this.canvas.style.width = `${this.cssW}px`;
    this.canvas.style.height = `${this.cssH}px`;
    // Reset and re-apply the DPR transform; the per-render
    // translate+scale we apply for the view sits on top of this.
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.Render();
  }

  // ============================================================
  // Event wiring
  // ============================================================

  private WireEvents(): void {
    // Pan: hold left, middle, OR right button and drag. Right-button
    // pan matches the CAD convention most users expect (matches the
    // 3D viewport's camera-controls right-drag too). The global
    // contextmenu suppression installed in App.ts means the right-
    // click never raises the browser menu, so the button is free
    // for navigation.
    this.canvas.addEventListener('mousedown', (e) => {
      // Only respond to the three standard mouse buttons.
      if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
      this.StopZoomAnim(); // a pan cancels any gliding zoom
      this.downButton = e.button;

      // A left press ON a view of an editable sheet picks the view up rather
      // than the paper. Every other press still pans, so the sheet can be moved
      // around under the cursor with the middle or right button wherever the
      // pointer happens to be.
      if (e.button === 0 && this.sheetEditable) {
        const hit = this.ViewAt(this.WorldFromClient(e.clientX, e.clientY));
        if (hit) {
          this.BeginViewDrag(hit, e.clientX, e.clientY);
          e.preventDefault();
          return;
        }
      }

      this.isPanning = true;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOrigViewX = this.viewX;
      this.panOrigViewY = this.viewY;
      this.canvas.classList.add('is-panning');
      e.preventDefault();
    });
    // Belt-and-braces: also stop the contextmenu locally so a right-
    // button-up at the end of a pan-drag doesn't surface the menu
    // even if the global suppression is somehow bypassed.
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    // Hover: outline the view the cursor is over, so it is plain what is about
    // to be picked up. Only while the sheet is editable — every other drawing
    // has no views and this costs it nothing.
    this.canvas.addEventListener('mousemove', (e) => {
      if (!this.sheetEditable || this.isPanning || this.draggingView) return;
      const hit = this.ViewAt(this.WorldFromClient(e.clientX, e.clientY));
      this.canvas.style.cursor = hit ? 'move' : '';
      if (hit !== this.hoverView) {
        this.hoverView = hit;
        this.Render();
      }
    });
    this.canvas.addEventListener('mouseleave', () => {
      if (this.hoverView && !this.draggingView) {
        this.hoverView = null;
        this.Render();
      }
    });
    window.addEventListener('mousemove', (e) => {
      if (this.draggingView) {
        this.dragDX = (e.clientX - this.panStartX) / this.viewScale;
        this.dragDY = (e.clientY - this.panStartY) / this.viewScale;
        this.Render();
        return;
      }
      if (!this.isPanning) return;
      this.viewX = this.panOrigViewX + (e.clientX - this.panStartX);
      this.viewY = this.panOrigViewY + (e.clientY - this.panStartY);
      // Only a drag that actually moved counts: a click-through pick must not
      // stop the drawing from re-fitting itself.
      if (Math.hypot(e.clientX - this.panStartX, e.clientY - this.panStartY) >= 4) {
        this.viewMovedByUser = true;
      }
      this.Render();
    });
    window.addEventListener('mouseup', (e) => {
      if (this.draggingView) {
        this.draggingView = false;
        this.canvas.classList.remove('is-panning');
        this.downButton = -1;

        const view = this.dragView;
        // A press that did not travel is not a move — and dropping a view a
        // pixel from where it started would still spend a re-compose on it.
        if (!view || Math.hypot(this.dragDX * this.viewScale, this.dragDY * this.viewScale) < 3) {
          this.ClearViewDrag();
          this.Render();
          return;
        }

        // The offset stays applied until the host sends the re-composed page
        // back (SetPageKeepView clears it), so the view does not flick back to
        // where it was for the width of a round trip.
        if (this.onViewMove) this.onViewMove(view.id, this.dragDX, this.dragDY);
        else this.ClearViewDrag();
        this.Render();
        return;
      }
      if (!this.isPanning) return;
      this.isPanning = false;
      this.canvas.classList.remove('is-panning');
      // A left press that didn't move is a pick, not a pan.
      const moved = Math.hypot(e.clientX - this.panStartX, e.clientY - this.panStartY);
      if (this.downButton === 0 && moved < 4 && this.onPick) {
        this.onPick(this.WorldFromClient(this.panStartX, this.panStartY));
      }
      // A right-click that did not pan is a request for the view's own menu.
      // The drag threshold is what keeps right-button panning: a right press
      // that travelled was navigation and raises nothing.
      if (this.downButton === 2 && moved < 4 && this.sheetEditable && this.onViewMenu) {
        const hit = this.ViewAt(this.WorldFromClient(this.panStartX, this.panStartY));
        if (hit) this.onViewMenu(hit, e.clientX, e.clientY);
      }
      this.downButton = -1;
    });
    // Wheel: zoom around the cursor.
    this.canvas.addEventListener(
      'wheel',
      (e) => {
        const rect = this.canvas.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        // deltaY > 0 → scroll down → zoom out.
        const factor = e.deltaY > 0 ? 1 / 1.15 : 1.15;
        this.ZoomBy(factor, cx, cy);
        e.preventDefault();
      },
      { passive: false },
    );
    // Double-click: fit to window.
    this.canvas.addEventListener('dblclick', () => {
      this.ZoomAll();
    });
  }

  /** Take hold of a view: remember which shapes are its, and where the drag
   *  started. The start point rides on the pan fields, which are free — a view
   *  drag and a pan are never both in flight. */
  private BeginViewDrag(view: SheetView, clientX: number, clientY: number): void {
    const count = this.page?.shapes.length ?? 0;
    const mask = new Array<boolean>(count).fill(false);

    for (const [from, to] of view.spans ?? []) {
      for (let i = Math.max(0, from); i < Math.min(count, to); i++) mask[i] = true;
    }

    this.dragView = view;
    this.dragMask = mask;
    this.dragDX = 0;
    this.dragDY = 0;
    this.draggingView = true;
    this.hoverView = view;
    this.panStartX = clientX;
    this.panStartY = clientY;
    this.canvas.classList.add('is-panning');
  }

  private WireResize(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        // An automatic view re-fits itself in Render(), so this only has to push
        // the new size through. A view the reader has moved is theirs and keeps
        // its pan and zoom across the resize — except on the way back from zero
        // size (a card that was collapsed, a torn-off panel remounting), where
        // there is no view worth keeping.
        if (this.cssW === 0 || this.cssH === 0) {
          this.pendingAutoFit = true;
          this.viewMovedByUser = false;
        }
        this.Resize();
      });
      // Observe the CONTAINER, not the canvas. Resize() sets the
      // canvas's inline style.width to a fixed pixel value (the
      // hi-DPI pattern: backing store at width×DPR, display at
      // width CSS px), which overrides the CSS width:100%. If we
      // observed the canvas, its bounding rect would stay frozen
      // once Resize had run once — collapsing the sidebar
      // wouldn't widen it. Observing the container catches every
      // container resize (sidebar collapse, window resize, panel
      // drag) and lets us push the new size onto the canvas.
      this.resizeObserver.observe(this.container);
    } else {
      // Fallback for older browsers — window resize only.
      window.addEventListener('resize', () => this.Resize());
    }
  }

  /** Track the effective colour scheme and repaint on change, so the 2D preview
   *  follows Settings ▸ UI Theme (and the OS under "System") just like the 3D
   *  viewport (see main.ts applyTheme). */
  private WireTheme(): void {
    this.dark = prefersDark();
    this.themeOff = watchTheme((): void => {
      this.dark = prefersDark();
      this.Render();
    });
  }
}

// =============================================================
// Helpers
// =============================================================

/** Default ink colour for shapes that don't specify one. Matches
 *  DrawingTypes.COLOR.ink so SVG + canvas previews look identical. */
const INK: Rgb = [0.08, 0.1, 0.13];

/** Flat dark drawing surface used in dark mode (kept in sync with the
 *  --canvas2d-bg token in modeler.css). Not pure black, so light-inverted ink
 *  still reads with comfortable contrast. */
const DARK_BG = '#1e1e1e';

/** Round a value UP to the nearest "nice" 1-2-5 × 10ⁿ number, so grid spacings
 *  land on readable intervals (…, 100, 200, 500, 1000, 2000, …). */
function niceInterval(v: number): number {
  if (!isFinite(v) || v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const f = v / base; // 1 .. <10
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * base;
}

/** Convert an `Rgb` triple (each component 0..1) to a CSS rgb()
 *  string. Used by every shape-style translator above. */
function rgbToCss(c: Rgb): string {
  const r = Math.round(c[0] * 255);
  const g = Math.round(c[1] * 255);
  const b = Math.round(c[2] * 255);
  return `rgb(${r},${g},${b})`;
}

/** Invert a colour's lightness (HSL L → 1−L) while preserving its hue and
 *  saturation. Black ink → white, dark grey → light grey, and a dark red →
 *  light red — so a drawing authored for a white sheet stays legible on the
 *  dark surface without recolouring genuinely coloured elements. */
function invertLightness(c: Rgb): Rgb {
  const [h, s, l] = rgbToHsl(c);
  return hslToRgb(h, s, 1 - l);
}

/** RGB (0..1) → HSL (h in 0..1, s/l in 0..1). */
function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l]; // achromatic (grey/black/white)
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

/** HSL (h/s/l in 0..1) → RGB (0..1). */
function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) return [l, l, l]; // achromatic
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}
